import { Injectable } from "@angular/core";
import {
	Completion,
	CourseFeedbacks,
	CourseListSimple,
	DistinctLearner,
	ExpiringCVCertificate,
	ImprovedBusinessScore,
	JAICEDetail,
	JAICEOutcomes,
	JAICERating,
	OrgCECompletion,
	OrgCompletion,
	OrgCourseVersionSessions,
	OrgNoActivity,
	OrphanedKey,
	ProductLineCECertificate,
	ProductLineCompletion,
	ProductLineSimple,
	QuestionMetric,
	ServiceResponse,
	TransferLikelihoodScore,
	UserReport
} from "@appcore/data";
import { LearningGainMetrics, ProductLineMetaDataRecord, VirtualHostingRecord } from "@data/insights";
import { Observable } from "rxjs";
import { API } from "@service/system/api.service";
import { HttpManager } from "@service/system/httpManager.service";
import { ResponseHandler } from "../system/responseHandler.service";

const API_URL = API.API_URL;

@Injectable()
export class InsightsService {
	private _insightsURL = API_URL + "/insight";
	private _hostingURL = API_URL + "/hosting";

	constructor(private httpManager: HttpManager, private responseHandler: ResponseHandler) {}

	/*  ----------  USER CREATED REPORTS  ----------  */

	getAggregateCoursesReports(userId: string): Observable<ServiceResponse<UserReport[]>> {
		return this.responseHandler.httpGet<UserReport[]>(`${this._insightsURL}/aggregatereports`, { userId: userId });
	}

	deleteReport(insightType: string, userId: string, reportId: string): Observable<number> {
		return this.httpManager.httpDelete<number>(`${this._insightsURL}/aggregatereports/delete`, {
			insightType: insightType,
			userId: userId,
			reportId: reportId
		});
	}

	/*  ----------  ASSESSMENT INSIGHTS  ----------  */

	getEvaluationMetrics(
		guid: string,
		startDate: string,
		endDate: string
	): Observable<ServiceResponse<QuestionMetric[]>> {
		const params = { startDate: startDate, endDate: endDate };
		return this.responseHandler.httpGet<QuestionMetric[]>(
			`${this._insightsURL}/assessments/${guid}/evaluationmetric`,
			params
		);
	}

	getQuestionMetrics(
		guid: string,
		startDate: string,
		endDate: string,
		ceOnly: any
	): Observable<ServiceResponse<QuestionMetric[]>> {
		const params = {
			startDate: startDate,
			endDate: endDate,
			ceOnly: ceOnly
		};
		return this.responseHandler.httpGet<QuestionMetric[]>(
			`${this._insightsURL}/assessments/${guid}/questionmetric`,
			params
		);
	}

	getCourseFeedback(
		guid: string,
		startDate: string,
		endDate: string
	): Observable<ServiceResponse<CourseFeedbacks[]>> {
		const params = { startDate: startDate, endDate: endDate };
		return this.responseHandler.httpGet<CourseFeedbacks[]>(
			`${this._insightsURL}/assessments/${guid}/questionnairememos`,
			params
		);
	}

	/*  ----------  ASSESSMENT INSIGHTS  ----------  */

	getExpiringCVCertificates(monthsOut: number): Observable<ServiceResponse<ExpiringCVCertificate[]>> {
		return this.responseHandler.httpGet<ExpiringCVCertificate[]>(
			`${this._insightsURL}/courseversion/certificates/expiring`,
			{ expiring: monthsOut }
		);
	}

	/*  ----------  EVALUATION INSIGHTS  ----------  */

	getLearningGainMetrics(CVItemID: string): Observable<ServiceResponse<LearningGainMetrics[]>> {
		return this.responseHandler.httpGet<LearningGainMetrics[]>(
			`${this._insightsURL}/evaluation/learninggainmetric`,
			{
				CVItemID: CVItemID
			}
		);
	}

	getTransferScore(CVItemID: string): Observable<ServiceResponse<TransferLikelihoodScore[]>> {
		return this.responseHandler.httpGet<TransferLikelihoodScore[]>(
			`${this._insightsURL}/evaluation/transferscore`,
			{
				CVItemID: CVItemID
			}
		);
	}

	getImprovedBusinessScore(CVItemID: string): Observable<ServiceResponse<ImprovedBusinessScore[]>> {
		return this.responseHandler.httpGet<ImprovedBusinessScore[]>(
			`${this._insightsURL}/evaluation/improvedbusiness`,
			{
				CVItemID: CVItemID
			}
		);
	}

	/*  ----------  AGGREGATE INSIGHTS  ----------  */

	createAggregateCoursesReports(body: string): Observable<ServiceResponse<string>> {
		return this.responseHandler.httpPost<string>(`${this._insightsURL}/aggregatereports/create`, body);
	}

	getReportableProductLines(): Observable<ServiceResponse<ProductLineSimple[]>> {
		return this.responseHandler.httpGet<ProductLineSimple[]>(`${this._insightsURL}/aggregatereports/productlines`);
	}

	/*  ----------  LEARNING OUTCOMES INSIGHTS  ----------  */

	createLearningOutcomesProductLineReport(body: string): Observable<ServiceResponse<string>> {
		return this.responseHandler.httpPost<string>(`${this._insightsURL}/outcomes/productline`, body);
	}

	createLearningOutcomesCourseReport(body: string): Observable<ServiceResponse<string>> {
		return this.responseHandler.httpPost<string>(`${this._insightsURL}/outcomes/course`, body);
	}

	/*  ----------  LEARNER TRANSACTIONAL INSIGHTS  ----------  */

	getCEAttempts(sdate: string, edate: string, cvid: string): Observable<ServiceResponse<any>> {
		return this.responseHandler.httpGet<any>(`${this._insightsURL}/ceattempts/${sdate}/${edate}/${cvid}/1/1000`);
	}

	getCECompletions(
		startDate: string,
		endDate: string,
		ceType: any,
		index: number,
		limit: number
	): Observable<ServiceResponse<any>> {
		return this.responseHandler.httpGet<any>(
			`${this._insightsURL}/cereport/${startDate}/${endDate}/43/GRADO/${ceType}/${index}/${limit}`
		);
	}

	getCompletions(
		startDate: string,
		endDate: string,
		ceType: number[],
		courseIDs: string
	): Observable<ServiceResponse<Completion[]>> {
		return this.responseHandler.httpGet<Completion[]>(
			`${this._insightsURL}/completions/${startDate}/${endDate}/1/1000/${ceType}/comp`,
			{ courseIDs: courseIDs }
		);
	}

	getCourseListSimple(): Observable<ServiceResponse<CourseListSimple[]>> {
		return this.responseHandler.httpGet<CourseListSimple[]>(`${this._insightsURL}/courselist`);
	}

	getCrceReportExport(startDate: string, endDate: string): any {
		return this.httpManager.httpPostBlob<any>(`${this._insightsURL}/export/crce/${startDate}/${endDate}`, null);
	}

	/*  ----------  ORGANIZATION TRANSACTIONAL INSIGHTS  ----------  */

	GetCEOrgCompletions(
		startDate: string,
		endDate: string,
		orgid: any,
		ceType: any
	): Observable<ServiceResponse<OrgCECompletion[]>> {
		return this.responseHandler.httpGet<OrgCECompletion[]>(
			`${this._insightsURL}/orgcecompletions/${startDate}/${endDate}/${orgid}/${ceType}/1/10000`
		);
	}

	getDistinctLearnersPerOrg(
		sDate: string,
		eDate: string,
		format: string
	): Observable<ServiceResponse<DistinctLearner[]>> {
		return this.responseHandler.httpGet<DistinctLearner[]>(
			`${this._insightsURL}/distinctlearners/${sDate}/${eDate}/${format}`
		);
	}

	getDistinctLearnerCEPerOrg(
		sDate: string,
		eDate: string,
		format: string
	): Observable<ServiceResponse<DistinctLearner[]>> {
		return this.responseHandler.httpGet<DistinctLearner[]>(
			`${this._insightsURL}/distinctcelearners/${sDate}/${eDate}/${format}`
		);
	}

	GetOrgCompletions(startDate: string, endDate: string, orgid: any): Observable<ServiceResponse<OrgCompletion[]>> {
		return this.responseHandler.httpGet<OrgCompletion[]>(
			`${this._insightsURL}/orgcompletions/${startDate}/${endDate}/${orgid}/1/10000`
		);
	}

	getOrgCourseVersionSessions(
		orgId: number,
		startDate: string,
		endDate: string
	): Observable<ServiceResponse<OrgCourseVersionSessions[]>> {
		const params = {
			orgID: orgId,
			startDate: startDate,
			endDate: endDate
		};
		return this.responseHandler.httpGet<OrgCourseVersionSessions[]>(`${this._insightsURL}/orgcvsessions`, params);
	}

	getOrgNoActivity(orgID: number, range: number): Observable<ServiceResponse<OrgNoActivity[]>> {
		const params = { orgID: orgID, range: range };
		return this.responseHandler.httpGet<OrgNoActivity[]>(`${this._insightsURL}/orgnoactivity`, params);
	}

	getOrhpanedKeys(): Observable<ServiceResponse<OrphanedKey[]>> {
		return this.responseHandler.httpGet<OrphanedKey[]>(`${this._insightsURL}/courseversion/keys/orphaned`);
	}

	/*  ----------  PRODUCT LINE TRANSACTIONAL INSIGHTS  ----------  */

	getProductLineCompletions(
		startDate: string,
		endDate: string,
		plItemID: string
	): Observable<ServiceResponse<ProductLineCompletion[]>> {
		return this.responseHandler.httpGet<ProductLineCompletion[]>(
			`${this._insightsURL}/plcompletions/${plItemID}/${startDate}/${endDate}`
		);
	}

	getProductLineCECertificate(plItemID: string): Observable<ServiceResponse<ProductLineCECertificate[]>> {
		return this.responseHandler.httpGet<ProductLineCECertificate[]>(
			`${this._insightsURL}/plcertificates/${plItemID}`
		);
	}

	getProductLineMetaData(plItemID: string): Observable<ServiceResponse<ProductLineMetaDataRecord[]>> {
		return this.responseHandler.httpGet<ProductLineMetaDataRecord[]>(
			`${this._insightsURL}/productlines/${plItemID}/versioncontent`
		);
	}

	/*  ----------  JAICE INSIGHTS  ----------  */

	getJAICEDetails(reportingYear: number): Observable<ServiceResponse<JAICEDetail[]>> {
		return this.responseHandler.httpGet<JAICEDetail[]>(`${this._insightsURL}/jaiceDetails`, {
			reportingYear: reportingYear
		});
	}

	getJAICEOutcomes(
		startDate: string,
		endDate: string,
		index: number,
		limit: number
	): Observable<ServiceResponse<JAICEOutcomes[]>> {
		const params = {
			startDate: startDate,
			endDate: endDate,
			index: index,
			limit: limit
		};
		return this.responseHandler.httpGet<JAICEOutcomes[]>(`${this._insightsURL}/jaiceoutcomes`, params);
	}

	getJAICERatings(reportingYear: number): Observable<ServiceResponse<JAICERating[]>> {
		return this.responseHandler.httpGet<JAICERating[]>(`${this._insightsURL}/jaiceratings`, {
			reportingYear: reportingYear
		});
	}

	/*  ----------  Virtual Hosting INSIGHTS  ----------  */

	getVirtualHostingInsight(): Observable<ServiceResponse<VirtualHostingRecord[]>> {
		return this.responseHandler.httpGet<VirtualHostingRecord[]>(`${this._hostingURL}/external/rpt_usage`);
	}
}
